/*!
 * Edible Growth — EG_INGREDIENTS
 * Single source of truth for all ingredient data site-wide.
 * Loaded deferred before modals.js. All product sections reference this.
 *
 * Each entry:
 *   category    — short ingredient class (e.g. "Cannabinoid", "Adaptogen")
 *   summary     — one-sentence hook shown beneath the title
 *   description — "What Is It?"  (factual definition)
 *   why         — "Why We Use It" (its role in the formula)
 *   wiifm       — "What's In It For Me?" (array of real-world outcomes)
 *   pairsWith   — "Pairs Well With" (array of other ingredient keys)
 *   imageUrl / imageAlt / color — media + accent
 */
(function () {
  'use strict';

  window.EG_INGREDIENTS = {

    /* ── Cannabinoids ──────────────────────────────────────────── */
    "CBD": {
      category: "Cannabinoid",
      summary: "A non-intoxicating hemp compound widely used to support calm, recovery, and balance.",
      description: "Cannabidiol (CBD) is the most-studied compound in hemp. It works with the body's own balancing systems and does not cause a high.",
      why: "It forms the calming, recovery-focused base of the formula, helping the body stay balanced while the other ingredients do their work.",
      wiifm: [
        "Feel calmer and more even, without sedation.",
        "Recover more comfortably after stress or training.",
        "Take the edge off tension and restlessness."
      ],
      pairsWith: ["CBG", "L-Theanine", "THC"],
      imageUrl: "edible-growth-cbd-molecular-structure.webp",
      imageAlt: "CBD (cannabidiol) molecular structure.",
      color: "#6ee7b7"
    },
    "CBG": {
      category: "Cannabinoid",
      summary: "The 'mother cannabinoid,' valued for clear-headed support and recovery.",
      description: "Cannabigerol (CBG) is a non-intoxicating hemp compound that other cannabinoids are made from. It's known for a clear, non-sedating feel.",
      why: "We include it for clean, focused support — it complements CBD by leaning toward clarity and physical recovery rather than relaxation.",
      wiifm: [
        "Stay clear-headed and focused.",
        "Bounce back faster from physical strain.",
        "Support without drowsiness."
      ],
      pairsWith: ["CBD", "L-Theanine"],
      imageUrl: "edible-growth-cbg-molecular-structure.webp",
      imageAlt: "CBG (cannabigerol) molecular structure.",
      color: "#93c5fd"
    },
    "CBG_Sleep": {
      category: "Cannabinoid",
      summary: "A non-intoxicating hemp compound that eases nighttime discomfort so rest comes easier.",
      description: "Cannabigerol (CBG) is a non-intoxicating hemp compound and a building block for other cannabinoids, often used to support comfort and balance.",
      why: "Lingering aches and tension can interrupt sleep. CBG helps quiet that physical discomfort so the body can settle.",
      wiifm: [
        "Settle into rest more comfortably.",
        "Fewer aches keeping you awake.",
        "Wake feeling more restored."
      ],
      pairsWith: ["CBN", "Magnesium Glycinate", "Reishi"],
      imageUrl: "edible-growth-cbg-molecular-structure.webp",
      imageAlt: "CBG (cannabigerol) molecular structure.",
      color: "#93c5fd"
    },
    "CBN": {
      category: "Cannabinoid",
      summary: "A gentle, mildly calming cannabinoid most associated with sleep.",
      description: "Cannabinol (CBN) forms naturally as THC ages. It's mildly relaxing and is the cannabinoid most often linked to restful sleep.",
      why: "It's the formula's wind-down anchor, encouraging the deep sense of calm that makes falling asleep feel natural.",
      wiifm: [
        "Drift off more easily.",
        "Feel deeply relaxed at bedtime.",
        "Stay asleep more soundly."
      ],
      pairsWith: ["CBG_Sleep", "Organic Valerian Root", "L-Theanine_Sleep"],
      imageUrl: "edible-growth-cbn-molecular-structure.webp",
      imageAlt: "CBN (cannabinol) molecular structure.",
      color: "#c4b5fd"
    },
    "THC": {
      category: "Cannabinoid",
      summary: "A trace, legal amount of hemp THC that rounds out the cannabinoid blend.",
      description: "Tetrahydrocannabinol (THC) is the active compound in cannabis. Kept under 0.3%, it stays within the legal hemp limit and is non-impairing here.",
      why: "A micro amount helps the other cannabinoids work better together — the 'entourage effect' — without a noticeable high.",
      wiifm: [
        "Get more from the full cannabinoid blend.",
        "Feel relaxed, not impaired.",
        "A smoother overall effect."
      ],
      pairsWith: ["CBD", "CBG"],
      imageUrl: "edible-growth-thc-molecular-structure.webp",
      imageAlt: "THC (tetrahydrocannabinol) molecular structure.",
      color: "#fca5a5"
    },

    /* ── Amino Acids & Nootropics ──────────────────────────────── */
    "L-Theanine": {
      category: "Amino Acid",
      summary: "A calming amino acid from tea that creates focus without jitters.",
      description: "L-Theanine is an amino acid found naturally in green tea, known for promoting a relaxed yet alert state.",
      why: "It smooths out the formula's energy and focus, keeping you sharp and composed rather than wired.",
      wiifm: [
        "Focus without feeling jittery.",
        "Stay calm under pressure.",
        "Clean, steady mental energy."
      ],
      pairsWith: ["Alpha GPC", "L-Tyrosine", "CBD"],
      imageUrl: "edible-growth-l-theanine-powder.webp",
      imageAlt: "L-Theanine powder used in Edible Growth formulas.",
      color: "#4ade80"
    },
    "L-Theanine_Sleep": {
      category: "Amino Acid",
      summary: "A calming amino acid from tea that quiets a busy mind at night.",
      description: "L-Theanine is an amino acid found naturally in green tea, known for promoting relaxation without making you drowsy.",
      why: "We use it to ease mental chatter before bed, helping you relax into sleep instead of forcing it.",
      wiifm: [
        "Quiet a racing mind at bedtime.",
        "Relax without grogginess.",
        "Ease into sleep naturally."
      ],
      pairsWith: ["CBN", "Organic Valerian Root", "Magnesium Glycinate"],
      imageUrl: "edible-growth-l-theanine-powder.webp",
      imageAlt: "L-Theanine powder used in Edible Growth formulas.",
      color: "#4ade80"
    },
    "L-Tyrosine": {
      category: "Amino Acid",
      summary: "An amino acid the body uses to make focus-and-motivation brain chemicals.",
      description: "L-Tyrosine is an amino acid and a building block for dopamine, a brain chemical tied to drive and mood.",
      why: "It supports steady motivation and mood during demanding training, especially when stress would otherwise wear you down.",
      wiifm: [
        "Stay motivated through tough sessions.",
        "Hold a positive, driven mood.",
        "Mental stamina under stress."
      ],
      pairsWith: ["Mucuna Pruriens", "L-Theanine", "Alpha GPC"],
      imageUrl: "edible-growth-l-tyrosine-powder.webp",
      imageAlt: "L-Tyrosine powder used in Edible Growth formulas.",
      color: "#fbbf24"
    },
    "L-Tyrosine_Gaming": {
      category: "Amino Acid",
      summary: "An amino acid that helps maintain focus and mood during long sessions.",
      description: "L-Tyrosine is an amino acid and a building block for dopamine, a brain chemical linked to focus and motivation.",
      why: "Long sessions drain mental drive. L-Tyrosine helps you stay locked in and even-keeled when it counts.",
      wiifm: [
        "Stay sharp deep into long sessions.",
        "Keep a level head in clutch moments.",
        "Sustained motivation."
      ],
      pairsWith: ["L-Theanine", "Mucuna Pruriens", "Rhodiola Rosea (3% Salidrosides)"],
      imageUrl: "edible-growth-l-tyrosine-powder.webp",
      imageAlt: "L-Tyrosine powder used in Edible Growth formulas.",
      color: "#fbbf24"
    },
    "L-Citrulline Malate 2:1": {
      category: "Amino Acid",
      summary: "An amino acid blend that improves blood flow for better pumps and endurance.",
      description: "L-Citrulline Malate combines an amino acid with malic acid in a 2:1 ratio to raise nitric oxide and support energy.",
      why: "It widens blood vessels so more oxygen and nutrients reach your muscles during training.",
      wiifm: [
        "Bigger, longer-lasting pumps.",
        "Push through more reps.",
        "Less fatigue mid-workout."
      ],
      pairsWith: ["Organic Beetroot Extract", "Glycerol Monostearate", "Beta-Alanine"],
      imageUrl: "edible-growth-l-citrulline-malate.webp",
      imageAlt: "L-Citrulline Malate powder used in Edible Growth formulas.",
      color: "#e879f9"
    },
    "Alpha GPC": {
      category: "Nootropic",
      summary: "A choline source that sharpens focus and the mind-muscle connection.",
      description: "Alpha GPC delivers choline to the brain, where it's used to make acetylcholine — a chemical involved in focus and muscle control.",
      why: "It tightens your focus in the gym so each movement feels deliberate and controlled.",
      wiifm: [
        "Lock in on your training.",
        "Stronger mind-muscle connection.",
        "Sharper, more precise lifts."
      ],
      pairsWith: ["Organic Lion's Mane", "L-Theanine", "L-Tyrosine"],
      imageUrl: "edible-growth-alpha-gpc-powder.webp",
      imageAlt: "Alpha GPC powder used in Edible Growth formulas.",
      color: "#0ea5e9"
    },
    "Alpha GPC_Gaming": {
      category: "Nootropic",
      summary: "A choline source that supports fast, accurate reactions.",
      description: "Alpha GPC delivers choline to the brain to make acetylcholine, a chemical involved in focus and quick motor response.",
      why: "It helps turn thought into action faster, so your inputs keep up with your decisions.",
      wiifm: [
        "React faster in-game.",
        "Crisper aim and control.",
        "Less mental lag."
      ],
      pairsWith: ["Lion's Mane", "L-Theanine", "B12-Methylcobalamin"],
      imageUrl: "edible-growth-alpha-gpc-powder.webp",
      imageAlt: "Alpha GPC powder used in Edible Growth formulas.",
      color: "#0ea5e9"
    },
    "Beta-Alanine": {
      category: "Amino Acid",
      summary: "An amino acid that buffers muscle fatigue so you can train longer.",
      description: "Beta-Alanine helps your muscles produce carnosine, which slows the build-up of acid that causes the burn during exercise.",
      why: "It delays that burning, drop-off feeling so you can squeeze out extra quality reps.",
      wiifm: [
        "Push past the burn.",
        "Get more reps per set.",
        "Train harder for longer."
      ],
      pairsWith: ["Creatine Monohydrate", "L-Citrulline Malate 2:1", "TMG (Betaine Anhydrous)"],
      imageUrl: "edible-growth-beta-alanine-powder.webp",
      imageAlt: "Beta-Alanine powder used in Edible Growth formulas.",
      color: "#38bdf8"
    },
    "TMG (Betaine Anhydrous)": {
      category: "Performance Ingredient",
      summary: "A beet-derived compound that supports strength, power, and hydration.",
      description: "Trimethylglycine (TMG), or betaine, is a compound found in beets that aids hydration and power output.",
      why: "It backs up creatine on strength and power while helping muscle cells stay hydrated under load.",
      wiifm: [
        "More power on big lifts.",
        "Better strength output.",
        "Improved muscle hydration."
      ],
      pairsWith: ["Creatine Monohydrate", "Beta-Alanine", "Glycerol Monostearate"],
      imageUrl: "edible-growth-betaine-anhydrous-tmg-powder.webp",
      imageAlt: "Betaine Anhydrous (TMG) powder used in Edible Growth formulas.",
      color: "#a5b4fc"
    },
    "Mucuna Pruriens": {
      category: "Performance Herb",
      summary: "A bean extract that's a natural source of the dopamine precursor L-DOPA.",
      description: "Mucuna Pruriens, or velvet bean, naturally contains L-DOPA, which the body converts into dopamine — a driver of motivation and focus.",
      why: "It provides an early lift in drive and focus, setting a motivated tone for performance.",
      wiifm: [
        "Feel motivated and driven.",
        "Sharper focus when it matters.",
        "A genuine mood lift."
      ],
      pairsWith: ["L-Tyrosine", "Rhodiola Rosea (3% Salidrosides)", "Alpha GPC"],
      imageUrl: "edible-growth-mucuna-pruriens-powder.webp",
      imageAlt: "Mucuna Pruriens powder used in Edible Growth formulas.",
      color: "#c4b5fd"
    },

    /* ── Vitamins & Minerals ───────────────────────────────────── */
    "B-12 Methylcobalamin": {
      category: "Vitamin",
      summary: "The most usable form of vitamin B12 for natural, stim-free energy.",
      description: "Methylcobalamin is a highly absorbable form of vitamin B12, essential for energy production and healthy nerves.",
      why: "It supports clean, stimulant-free energy so the formula can keep you going without caffeine.",
      wiifm: [
        "Steady energy without a crash.",
        "Less stim-free sluggishness.",
        "Support for nerve and brain function."
      ],
      pairsWith: ["Cordyceps", "Magnesium Malate", "L-Tyrosine"],
      imageUrl: "edible-growth-b12-methylcobalamin-powder.webp",
      imageAlt: "Vitamin B12 (methylcobalamin) powder used in Edible Growth formulas.",
      color: "#f472b6"
    },
    "B12-Methylcobalamin": {
      category: "Vitamin",
      summary: "The most usable form of vitamin B12 for energy and quick thinking.",
      description: "Methylcobalamin is a highly absorbable form of vitamin B12, important for energy and healthy nerve signaling.",
      why: "Healthy nerve signaling underpins fast reactions, and B12 helps keep that system well-supplied.",
      wiifm: [
        "Stay alert and responsive.",
        "Stim-free energy for long sessions.",
        "Support for sharp reactions."
      ],
      pairsWith: ["Alpha GPC_Gaming", "Lion's Mane", "Magnesium Malate"],
      imageUrl: "edible-growth-b12-methylcobalamin-powder.webp",
      imageAlt: "Vitamin B12 (methylcobalamin) powder used in Edible Growth formulas.",
      color: "#f472b6"
    },
    "Magnesium Glycinate": {
      category: "Mineral",
      summary: "A gentle, well-absorbed magnesium known for calming body and mind.",
      description: "Magnesium Glycinate pairs magnesium with the amino acid glycine — a form that's easy on the stomach and calming.",
      why: "It relaxes muscles and the nervous system at night, setting the stage for deeper sleep.",
      wiifm: [
        "Relax tense muscles.",
        "Calm the nervous system before bed.",
        "Sleep more deeply."
      ],
      pairsWith: ["CBN", "L-Theanine_Sleep", "Organic Valerian Root"],
      imageUrl: "edible-growth-magnesium-glycinate-powder.webp",
      imageAlt: "Magnesium Glycinate powder used in Edible Growth formulas.",
      color: "#a78bfa"
    },
    "Magnesium Malate": {
      category: "Mineral",
      summary: "A form of magnesium that supports muscle function and cellular energy.",
      description: "Magnesium Malate combines magnesium with malic acid, which plays a role in your body's energy production.",
      why: "It supports energy and muscle function while keeping you calm-but-alert, with no sedation.",
      wiifm: [
        "Support steady energy.",
        "Help muscles work smoothly.",
        "Stay relaxed yet alert."
      ],
      pairsWith: ["B-12 Methylcobalamin", "Potassium Citrate", "Calcium Citrate"],
      imageUrl: "edible-growth-magnesium-malate-powder.webp",
      imageAlt: "Magnesium Malate powder used in Edible Growth formulas.",
      color: "#c084fc"
    },
    "Creatine Monohydrate": {
      category: "Performance Ingredient",
      summary: "The most researched supplement in sports nutrition — proven to build strength, power, and size.",
      description: "Creatine monohydrate is a naturally occurring compound stored in your muscles, where it rapidly regenerates the energy used during short, explosive efforts like lifting and sprinting.",
      why: "It's the strength backbone of the formula. By topping off your muscles' rapid-energy stores, creatine lets you train harder and recover faster between efforts.",
      wiifm: [
        "Lift heavier and grind out more reps before fatigue sets in.",
        "Build lean muscle size and strength faster over time.",
        "Refuel your muscles' explosive energy between sets."
      ],
      pairsWith: ["Beta-Alanine", "L-Citrulline Malate 2:1", "TMG (Betaine Anhydrous)"],
      imageUrl: "edible-growth-creatine-monohydrate.webp",
      imageAlt: "Creatine monohydrate powder used in Edible Growth formulas.",
      imagePosition: "50% 30%",
      color: "#e2e8f0"
    },
    "Glycerol Monostearate": {
      category: "Performance Ingredient",
      summary: "A hydration compound that pulls water into muscle for fuller pumps.",
      description: "Glycerol Monostearate helps draw and hold water in your muscle cells, creating a 'hyper-hydrated' state.",
      why: "It amplifies pumps and endurance by keeping muscles saturated with fluid during training.",
      wiifm: [
        "Fuller, longer-lasting pumps.",
        "Better endurance.",
        "Improved hydration while you train."
      ],
      pairsWith: ["L-Citrulline Malate 2:1", "Organic Beetroot Extract", "TMG (Betaine Anhydrous)"],
      imageUrl: "edible-growth-glycerol-monostearate.webp",
      imageAlt: "Glycerol Monostearate used in Edible Growth formulas.",
      imagePosition: "60% 70%",
      imageScale: 1.18,
      color: "#94a3b8"
    },
    "Pink Himalayan Sea Salt": {
      category: "Electrolyte",
      summary: "An unrefined salt that replaces electrolytes lost through sweat.",
      description: "Pink Himalayan salt is an unrefined salt containing sodium and trace minerals that support hydration.",
      why: "It replenishes the sodium you lose when you sweat, helping prevent cramps and performance dips.",
      wiifm: [
        "Stay hydrated through tough workouts.",
        "Fewer cramps.",
        "Maintain peak output."
      ],
      pairsWith: ["Potassium Citrate", "Calcium Citrate", "Glycerol Monostearate"],
      imageUrl: "edible-growth-pink-himalayan-sea-salt.webp",
      imageAlt: "Pink Himalayan sea salt used in Edible Growth formulas.",
      color: "#fda4af"
    },
    "Potassium Citrate": {
      category: "Electrolyte",
      summary: "A key electrolyte for hydration, nerve signals, and muscle contraction.",
      description: "Potassium is an essential electrolyte that helps regulate fluid balance and proper muscle and nerve function.",
      why: "Training depletes potassium; topping it up keeps muscles firing and reduces cramping.",
      wiifm: [
        "Help prevent cramps.",
        "Smoother muscle contractions.",
        "Better hydration balance."
      ],
      pairsWith: ["Pink Himalayan Sea Salt", "Calcium Citrate", "Magnesium Malate"],
      imageUrl: "edible-growth-potassium-citrate-powder.webp",
      imageAlt: "Potassium Citrate powder used in Edible Growth formulas.",
      color: "#f5d0fe"
    },
    "Potassium Citrate_Gaming": {
      category: "Electrolyte",
      summary: "A key electrolyte that keeps muscles and nerves firing accurately.",
      description: "Potassium is an essential electrolyte that supports fluid balance and proper nerve and muscle function.",
      why: "Steady potassium levels keep small, precise movements smooth during long sessions.",
      wiifm: [
        "Avoid mid-session cramps.",
        "Keep movements smooth and precise.",
        "Maintain hydration balance."
      ],
      pairsWith: ["Calcium Citrate_Gaming", "Magnesium Malate", "B12-Methylcobalamin"],
      imageUrl: "edible-growth-potassium-citrate-powder.webp",
      imageAlt: "Potassium Citrate powder used in Edible Growth formulas.",
      color: "#f5d0fe"
    },
    "Calcium Citrate": {
      category: "Mineral",
      summary: "A well-absorbed calcium important for strong muscle contractions.",
      description: "Calcium Citrate is an absorbable form of calcium, a mineral essential for muscle contraction and nerve signaling.",
      why: "Calcium is what tells muscles to contract; keeping it topped up supports powerful, consistent reps.",
      wiifm: [
        "Powerful, consistent contractions.",
        "Support from the first rep to the last.",
        "Healthy nerve-to-muscle signaling."
      ],
      pairsWith: ["Potassium Citrate", "Pink Himalayan Sea Salt", "Magnesium Malate"],
      imageUrl: "edible-growth-calcium-citrate-powder.webp",
      imageAlt: "Calcium Citrate powder used in Edible Growth formulas.",
      color: "#d1d5db"
    },
    "Calcium Citrate_Gaming": {
      category: "Mineral",
      summary: "A well-absorbed calcium that helps muscles fire quickly and precisely.",
      description: "Calcium Citrate is an absorbable form of calcium, a mineral essential for muscle contraction and nerve signaling.",
      why: "Calcium lets muscles fire on command — important for crisp aim and fast reflexes.",
      wiifm: [
        "Keep reflexes crisp.",
        "Precise, responsive movement.",
        "Steady control late in sessions."
      ],
      pairsWith: ["Potassium Citrate_Gaming", "Magnesium Malate", "Alpha GPC_Gaming"],
      imageUrl: "edible-growth-calcium-citrate-powder.webp",
      imageAlt: "Calcium Citrate powder used in Edible Growth formulas.",
      color: "#d1d5db"
    },

    /* ── Mushrooms ─────────────────────────────────────────────── */
    "Organic Lion's Mane": {
      category: "Nootropic Mushroom",
      summary: "A mushroom traditionally used to support memory and focus.",
      description: "Lion's Mane is an edible mushroom studied for its support of cognition, focus, and nerve health.",
      why: "It sharpens concentration in the gym, helping you stay dialed into each set.",
      wiifm: [
        "Concentrate on your training.",
        "Clearer focus during workouts.",
        "Better mind-muscle connection."
      ],
      pairsWith: ["Alpha GPC", "Rhodiola Rosea (3% Rosavins)", "L-Theanine"],
      imageUrl: "edible-growth-lions-mane-mushroom.webp",
      imageAlt: "Lion's Mane mushroom ingredient used in Edible Growth formulas.",
      color: "#f59e0b"
    },
    "Lion's Mane": {
      category: "Nootropic Mushroom",
      summary: "A mushroom traditionally used to support focus and quick thinking.",
      description: "Lion's Mane is an edible mushroom studied for its support of memory, focus, and nerve health.",
      why: "It supports the fast decision-making and clarity competitive gaming demands.",
      wiifm: [
        "Think clearly under pressure.",
        "Faster decisions in-game.",
        "Sustained mental focus."
      ],
      pairsWith: ["Alpha GPC_Gaming", "Rhodiola Rosea (3% Salidrosides)", "B12-Methylcobalamin"],
      imageUrl: "edible-growth-lions-mane-mushroom.webp",
      imageAlt: "Lion's Mane mushroom ingredient used in Edible Growth formulas.",
      color: "#f59e0b"
    },
    "Reishi": {
      category: "Functional Mushroom",
      summary: "An adaptogenic mushroom used to support calm, immunity, and recovery.",
      description: "Reishi is an adaptogenic mushroom traditionally used to help the body handle stress and support immune health.",
      why: "It supports whole-body recovery and a calmer stress response so you restore more fully.",
      wiifm: [
        "Recover more completely.",
        "Feel more resilient to stress.",
        "Everyday immune support."
      ],
      pairsWith: ["Turkey Tail", "Cordyceps", "Tart Cherry Extract"],
      imageUrl: "edible-growth-reishi-mushroom.webp",
      imageAlt: "Reishi mushroom ingredient used in Edible Growth formulas.",
      color: "#b91c1c"
    },
    "Turkey Tail": {
      category: "Functional Mushroom",
      summary: "A functional mushroom valued for gut and immune support.",
      description: "Turkey Tail is a functional mushroom rich in polysaccharides, traditionally used for immune and gut health.",
      why: "It supports gut balance and overnight recovery as part of the formula's whole-body restore.",
      wiifm: [
        "Support gut and immune health.",
        "Aid overnight recovery.",
        "Feel more restored by morning."
      ],
      pairsWith: ["Reishi", "CBG_Sleep", "Tart Cherry Extract_Sleep"],
      imageUrl: null,
      imageAlt: "Turkey Tail extract powder",
      color: "#a1a1aa"
    },
    "Cordyceps": {
      category: "Functional Mushroom",
      summary: "A mushroom used to support stamina and oxygen efficiency.",
      description: "Cordyceps is a functional mushroom studied for supporting energy, stamina, and how well the body uses oxygen.",
      why: "It supports endurance, helping you sustain effort deeper into training.",
      wiifm: [
        "Last longer before fatigue.",
        "Better stamina.",
        "Steadier energy during cardio or lifts."
      ],
      pairsWith: ["B-12 Methylcobalamin", "Reishi", "Organic Beetroot Extract"],
      imageUrl: "edible-growth-cordyceps-mushroom.webp",
      imageAlt: "Cordyceps mushroom ingredient used in Edible Growth formulas.",
      color: "#f97316"
    },

    /* ── Adaptogens & Botanicals ───────────────────────────────── */
    "Rhodiola Rosea (3% Rosavins)": {
      category: "Adaptogen",
      summary: "An adaptogen standardized to reduce physical fatigue and perceived effort.",
      description: "Rhodiola Rosea is a stress-adapting herb; the Rosavins form is associated with reducing physical fatigue.",
      why: "It lowers how hard training feels, so you can keep your intensity up when it matters.",
      wiifm: [
        "Training feels less draining.",
        "Push for those last reps.",
        "Better resilience to stress."
      ],
      pairsWith: ["Rhodiola Rosea (3% Salidrosides)", "Cordyceps", "Organic Lion's Mane"],
      imageUrl: "edible-growth-rhodiola-rosea-rosavins.webp",
      imageAlt: "Rhodiola Rosea (Rosavins) root extract used in Edible Growth formulas.",
      color: "#ec4899"
    },
    "Rhodiola Rosea (3% Salidrosides)": {
      category: "Adaptogen",
      summary: "An adaptogen standardized to support mental stamina and focus under stress.",
      description: "Rhodiola Rosea is a stress-adapting herb; the Salidrosides form is linked to mental endurance and focus.",
      why: "It helps the mind stay sharp and composed when fatigue and pressure set in.",
      wiifm: [
        "Stay focused when tired.",
        "Keep calm under pressure.",
        "Mental endurance for the long haul."
      ],
      pairsWith: ["Rhodiola Rosea (3% Rosavins)", "L-Theanine", "Lion's Mane"],
      imageUrl: "edible-growth-rhodiola-rosea-salidrosides.webp",
      imageAlt: "Rhodiola Rosea (Salidrosides) root extract used in Edible Growth formulas.",
      color: "#d946ef"
    },
    "Organic Valerian Root": {
      category: "Sleep Herb",
      summary: "A traditional herb used to ease restlessness and help you fall asleep.",
      description: "Valerian root is an herb long used to calm restlessness and support falling asleep.",
      why: "It shortens the wind-down, helping quiet the body and mind so sleep comes sooner.",
      wiifm: [
        "Fall asleep faster.",
        "Feel calmer at bedtime.",
        "Less restlessness."
      ],
      pairsWith: ["CBN", "L-Theanine_Sleep", "Magnesium Glycinate"],
      imageUrl: "edible-growth-valerian-root-powder.webp",
      imageAlt: "Valerian root powder used in Edible Growth formulas.",
      color: "#a16207"
    },
    "Organic Beetroot Extract": {
      category: "Performance Botanical",
      summary: "A nitrate-rich extract that boosts blood flow and oxygen delivery.",
      description: "Beetroot extract is rich in nitrates, which the body turns into nitric oxide to improve circulation.",
      why: "It opens up blood flow for stronger pumps and better oxygen delivery to working muscles.",
      wiifm: [
        "Stronger muscle pumps.",
        "Better endurance.",
        "More oxygen to your muscles."
      ],
      pairsWith: ["L-Citrulline Malate 2:1", "Glycerol Monostearate", "Cordyceps"],
      imageUrl: "edible-growth-beet-root-powder.webp",
      imageAlt: "Beet root powder used in Edible Growth formulas.",
      imagePosition: "55% 42%",
      color: "#be123c"
    },

    /* ── Extracts ──────────────────────────────────────────────── */
    "Tart Cherry Extract": {
      category: "Recovery Herb",
      summary: "An antioxidant-rich fruit extract that helps with post-workout soreness.",
      description: "Tart cherry extract is concentrated from tart cherries and is rich in antioxidants and anti-inflammatory compounds.",
      why: "It targets exercise-induced soreness so you recover faster between sessions.",
      wiifm: [
        "Less next-day soreness.",
        "Recover faster between workouts.",
        "Feel fresher for your next session."
      ],
      pairsWith: ["Reishi", "CBD", "CBG"],
      imageUrl: "edible-growth-tart-cherry-extract.webp",
      imageAlt: "Tart cherry extract powder used in Edible Growth formulas.",
      color: "#9f1239"
    },
    "Tart Cherry Extract_Sleep": {
      category: "Recovery Herb",
      summary: "An antioxidant-rich fruit extract that supports recovery and nighttime comfort.",
      description: "Tart cherry extract is concentrated from tart cherries, rich in antioxidants often used in recovery formulas.",
      why: "At night it supports muscle recovery and comfort, helping you wake less worn down.",
      wiifm: [
        "Wake feeling less sore.",
        "Support overnight recovery.",
        "More comfortable rest."
      ],
      pairsWith: ["Reishi", "CBG_Sleep", "Turkey Tail"],
      imageUrl: "edible-growth-tart-cherry-extract.webp",
      imageAlt: "Tart cherry extract powder used in Edible Growth formulas.",
      color: "#be123c"
    },
    "Pumpkin Seed Extract": {
      category: "Bladder Support Herb",
      summary: "A plant extract used to support bladder and urinary comfort.",
      description: "Pumpkin seed extract is a plant extract traditionally used to support bladder and urinary function.",
      why: "It helps reduce nighttime bathroom trips so sleep is interrupted less often.",
      wiifm: [
        "Fewer nighttime bathroom trips.",
        "Sleep through the night more often.",
        "Everyday bladder comfort."
      ],
      pairsWith: ["Corn Silk Extract", "Horsetail Extract", "CBN"],
      imageUrl: null,
      imageAlt: "Pumpkin Seed Extract powder",
      color: "#22c55e"
    },
    "Corn Silk Extract": {
      category: "Bladder Support Herb",
      summary: "A botanical traditionally used for urinary and bladder wellness.",
      description: "Corn silk is the soft fibers of the corn plant, traditionally used to support urinary tract comfort.",
      why: "It supports bladder function so you can stay asleep with fewer disruptions.",
      wiifm: [
        "Fewer sleep interruptions.",
        "Support urinary comfort.",
        "Stay asleep longer."
      ],
      pairsWith: ["Pumpkin Seed Extract", "Horsetail Extract", "CBN"],
      imageUrl: null,
      imageAlt: "Corn Silk Extract powder",
      color: "#fef08a"
    },
    "Horsetail Extract": {
      category: "Bladder Support Herb",
      summary: "A silica-containing botanical used to support healthy fluid balance.",
      description: "Horsetail is a plant extract naturally containing silica, traditionally used for fluid balance and urinary support.",
      why: "It rounds out the bladder-support blend, helping reduce frequent wake-ups.",
      wiifm: [
        "Reduce frequent wake-ups.",
        "Support healthy fluid balance.",
        "More uninterrupted sleep."
      ],
      pairsWith: ["Pumpkin Seed Extract", "Corn Silk Extract", "Magnesium Glycinate"],
      imageUrl: null,
      imageAlt: "Horsetail Extract powder",
      color: "#10b981"
    }

  };

  /* Resolve bare image filenames to the theme's /assets/ URL, then warm the
     browser cache so ingredient popups (and prev/next) show images instantly
     on first open. */
  (function () {
    var base = window.EG_ASSET_BASE || '';
    var store = window.EG_INGREDIENTS, urls = {};
    Object.keys(store).forEach(function (k) {
      var u = store[k].imageUrl;
      if (u && u.indexOf('//') === -1 && u.indexOf('http') === -1) {
        store[k].imageUrl = base + u;
      }
      if (store[k].imageUrl) urls[store[k].imageUrl] = 1;
    });
    function warm() {
      Object.keys(urls).forEach(function (src) {
        var img = new Image();
        img.decoding = 'async';
        img.src = src;
      });
    }
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 });
    else setTimeout(warm, 1200);
  })();

})();
